import React, { useState, useEffect } from 'react';

import DatePicker from 'react-date-picker';
import 'react-day-picker/lib/style.css';

import { apiGet, getAccountsApiUrl } from '../common/AppApi';
import { selectOptions } from '../common/Forms';
import Strings from '../common/Strings';
import MessageTag from '../common/MessageTag';
import Processing from '../common/Processing';
import Pagination, { resetPaging } from '../common/Pagination';
import { PageSize } from '../common/Constants';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function BillingReport() {
	let [programs, setPrograms] = useState([]);
	let [programUuid, setProgramUuid] = useState('');
	let [startDate, setStartDate] = useState(null);
	let [endDate, setEndDate] = useState(null);
	let [message, setMessage] = useState();
	let [showSpinner, setSpinner] = useState(false);
	let [results, setResults] = useState(null);
	let [pagingData, setPagingData] = useState(resetPaging());

	useEffect(() => {
		let url = getAccountsApiUrl() + '/dash';
		apiGet(url).then((resp) => {
			let previousProgram = null;
			let progs = [{ programUuid: '', programName: 'Select...' }];
			resp.forEach((item) => {
				if (item.programUuid != previousProgram) {
					previousProgram = item.programUuid;
					progs.push({ programUuid: item.programUuid, programName: item.programName });
				}
			});
			setPrograms(progs);
		}).catch((err) => {
			console.error(err);
		});
	}, []);

	let formatDate = (date) => {
		let d = new Date(date);
		let month = String(d.getMonth() + 1).padStart(2, '0');
		let day = String(d.getDate()).padStart(2, '0');
		return d.getFullYear() + '-' + month + '-' + day;
	};

	let buildQueryParams = () => {
		let queryParams = [];
		if (programUuid)
			queryParams.push('programUuid=' + programUuid);
		if (startDate)
			queryParams.push('startDate=' + formatDate(startDate));
		if (endDate)
			queryParams.push('endDate=' + formatDate(endDate));
		return queryParams.join('&');
	};

	let handleDisplayReport = () => {
		if (!programUuid) {
			setMessage({ text: 'Please select a program.', type: 'warning' });
			return;
		}
		if (!startDate || !endDate) {
			setMessage({ text: 'Please select both a start date and an end date.', type: 'warning' });
			return;
		}
		if (endDate < startDate) {
			setMessage({ text: 'End date must be on or after the start date.', type: 'warning' });
			return;
		}

		setMessage(null);
		setSpinner(true);

		let url = getAccountsApiUrl() + '/dash/recipient-report?' + buildQueryParams();
		apiGet(url).then((resp) => {
			if (!resp || resp.length === 0) {
				setResults(null);
				setMessage({ text: 'No billing report available for the selected program and date range.', type: 'warning' });
			} else {
				setResults(resp);
				setPagingData(resetPaging());
			}
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'danger' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let columns = [
		{ key: 'program',              label: 'Program' },
		{ key: 'recipientId',          label: 'Recipient ID' },
		{ key: 'recipientName',        label: 'Recipient Name' },
		{ key: 'accountStatus',        label: 'Account Status' },
		{ key: 'benefitIssuanceCount', label: 'Benefit Issuance Count' },
		{ key: 'benefitAvailableDate', label: 'Benefit Available Date' },
		{ key: 'benefitExpirationDate',label: 'Benefit Expiration Date' },
		{ key: 'transactionCount',     label: 'Transaction Count' },
		{ key: 'lastTransactionDateTime', label: 'Last Transaction Date/Time' },
		{ key: 'benefitIssuanceTotal', label: 'Benefit Issuance Total' },
		{ key: 'periodEndBalance',     label: 'Period End Balance' },
	];

	let nextPage = () => {
		let next = { ...pagingData, currentPage: pagingData.currentPage + 1 };
		setPagingData(next);
	};

	let prevPage = () => {
		let prev = { ...pagingData, currentPage: pagingData.currentPage - 1 };
		setPagingData(prev);
	};

	let gotoPage = (pageNo) => {
		setPagingData({ ...pagingData, currentPage: pageNo });
	};

	let exportExcel = () => {
		let rows = results.map((row) =>
			columns.reduce((obj, col) => {
				obj[col.label] = row[col.key] != null ? row[col.key] : '';
				return obj;
			}, {})
		);
		let ws = XLSX.utils.json_to_sheet(rows);
		let wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Billing Report');
		XLSX.writeFile(wb, 'billing-report.xlsx');
	};

	let exportPdf = () => {
		let doc = new jsPDF({ orientation: 'landscape' });
		doc.text('Billing Report', 14, 14);
		doc.autoTable({
			startY: 20,
			headStyles: { fillColor: [52, 58, 64] },
			head: [columns.map((col) => col.label)],
			body: results.map((row) =>
				columns.map((col) => (row[col.key] != null ? String(row[col.key]) : ''))
			),
		});
		doc.save('billing-report.pdf');
	};

	let ResultsTable = () => {
		if (results === null) return null;

		let pageStart = (pagingData.currentPage - 1) * PageSize;
		let pageRows = results.slice(pageStart, pageStart + PageSize);
		let pageCount = Math.ceil(results.length / PageSize);

		return (
			<>
			<div className="table-responsive mt-3">
				<table className="table table-bordered table-striped table-sm">
					<thead className="thead-dark">
						<tr>
							{columns.map((col) => (
								<th key={col.key}>{col.label}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{pageRows.map((row, index) => (
							<tr key={index}>
								{columns.map((col) => (
									<td key={col.key}>{row[col.key] != null ? String(row[col.key]) : ''}</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		<div className="mt-2 mb-2">
			Export:&nbsp;
			<button className="btn btn-sm btn-primary" onClick={exportExcel}>Excel</button>
			&nbsp;
			<button className="btn btn-sm btn-primary" onClick={exportPdf}>PDF</button>
		</div>		<Pagination recordCount={results.length} pageCount={pageCount} pagingData={pagingData}
			setPagingData={() => {}} pageSize={PageSize}				nextPage={nextPage} prevPage={prevPage} gotoPage={gotoPage} />
			</>
		);
	};

	return (
		<div className="container mt-4">
			<h4>Billing Report</h4>
			<hr />
			<Processing show={showSpinner} />
			<MessageTag message={message} />

			<div className="form-row align-items-end">
				<div className="form-group col-md-3">
					<label htmlFor="programUuid">Program</label>
					<select id="programUuid" className="form-control" value={programUuid} onChange={(e) => setProgramUuid(e.target.value)}>
						{selectOptions(programs, 'programUuid', 'programName')}
					</select>
				</div>
				<div className="form-group col-md-3">
					<label htmlFor="startDate">Start Date</label>
					<DatePicker
						id="startDate"
						name="startDate"
						value={startDate}
						onChange={(val) => setStartDate(val)}
						className="form-control"
						format="yyyy-MM-dd"
						monthPlaceholder="MM"
						yearPlaceholder="YYYY"
						dayPlaceholder="DD"
					/>
				</div>
				<div className="form-group col-md-3">
					<label htmlFor="endDate">End Date</label>
					<DatePicker
						id="endDate"
						name="endDate"
						value={endDate}
						onChange={(val) => setEndDate(val)}
						className="form-control"
						format="yyyy-MM-dd"
						monthPlaceholder="MM"
						yearPlaceholder="YYYY"
						dayPlaceholder="DD"
					/>
				</div>
				<div className="form-group col-md-2">
					<button
						className="btn btn-primary"
						onClick={handleDisplayReport}
						disabled={showSpinner}
					>
						Display Report
					</button>
				</div>
			</div>

			<ResultsTable />
		</div>
	);
}
